<template>
  <div class="vcs-root currency-selector">
    <v-select
      :modelValue="currency"
      @update:modelValue="setValue('currency', $event)"
      class="v-select-custom-styles"
      :options="currencyOptions"
      :reduce="reduceCurrencyOption"
      label="label"
      :clearable="false"
    >
      <template #selected-option="{ label, flagClass }">
        <div class="vcs-select-option">
          <div class="currency-flag" :class="flagClass"></div>
          <span class="vcs-select-option-desc">{{ label }}</span>
        </div>
      </template>
      <template #option="{ label, flagClass }">
        <div class="vcs-select-option">
          <div class="currency-flag" :class="flagClass"></div>
          <span class="vcs-select-option-desc">{{ label }}</span>
        </div>
      </template>
    </v-select>
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue'
import vSelect from 'vue-select'
import { usePaymentForm } from '~/shared/use-payment-form'

type CurrencyOption = {
  value: string
  label: string
  flagClass: string
}

const props = defineProps<{
  field?: string
  currencies?: string[]
}>()

const { form, setValue } = usePaymentForm()

const currency = computed(() => form.value.currency || 'USD')
const currencyOptions = computed<CurrencyOption[]>(() =>
  (props.currencies || ['USD']).map((code) => ({
    value: code,
    label: code,
    flagClass: `currency-flag-${code.toLowerCase()}`
  }))
)

const reduceCurrencyOption = (option: CurrencyOption) => option.value
</script>
<style lang="scss">
@import 'vue-select/dist/vue-select.css';
</style>
<style scoped lang="scss">
.vcs-root {
  width: 160px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 42px;
}

.currency-selector :deep(.v-select) {
  /* vue-select defines these on :root; shadow DOM needs local fallbacks. */
  --vs-border-color: #ddd;
  --vs-border-width: 1px;
  --vs-border-style: solid;
  --vs-border-radius: 5px;
  --vs-actions-padding: 4px 6px 0 3px;
  --vs-controls-color: rgba(60, 60, 60, 0.5);
  --vs-selected-bg: #f0f0f0;
  --vs-selected-color: #333;
  --vs-selected-border-color: var(--vs-border-color);
  --vs-selected-border-style: var(--vs-border-style);
  --vs-selected-border-width: var(--vs-border-width);
  --vs-dropdown-bg: #fff;
  --vs-dropdown-color: inherit;
  --vs-dropdown-z-index: 1000;
  --vs-dropdown-min-width: 160px;
  --vs-dropdown-max-height: 350px;
  --vs-dropdown-box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.15);
  --vs-dropdown-option-padding: 3px 20px;
  --vs-line-height: 1.4;
  --vs-font-size: 1rem;
  background: #f7f9fb;
  height: 42px;
}

.currency-selector :deep(.v-select-custom-styles) {
  width: 160px;
}

.currency-selector :deep(.vs__dropdown-toggle) {
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 0 0 4px;
}

.currency-selector :deep(.vs__selected-options) {
  padding: 0 2px;
}

.currency-selector :deep(.vs__search),
.currency-selector :deep(.vs__search:focus) {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  line-height: 1.85;
  font-size: 1em;
  border: 1px solid transparent !important;
  border-left: none !important;
  outline: none !important;
  margin: 4px 0 0 !important;
  padding: 0 7px !important;
  background: none !important;
  box-shadow: none;
  width: 0;
  max-width: 100%;
  flex-grow: 1;
}

.vcs-select-option {
  display: flex;
  align-items: center;
}

.vcs-select-option .vcs-select-option-desc {
  margin-left: 1rem;
}

.currency-flag {
  display: inline-block;
  width: 24px;
  height: 16px;
  background-size: cover;
}

.currency-flag.currency-flag-usd {
  background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAgBAMAAACm+uYvAAAAD1BMVEUAJWq0vtO1v9PBASv////7AfyZAAAAOUlEQVQoz2NgQALGSIABt4QSHDK4IAEGBkU4RJMg3Q4kS9CMQlhCqR3DxB8uOABuCWMcALfEiLUDADkOhlU5rxL6AAAAAElFTkSuQmCC");
}

.currency-flag.currency-flag-ils {
  background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAgCAMAAABjCgsuAAAAPFBMVEUAMrsGN70MPL4NPb8OPr8PPr8RQMAmUcZKbs91kdt5lNx6ldyKoeC1xOzZ4fXn7Pny9fz09/35+/7///+pkLZFAAAAeklEQVQ4y+2UzQqAMAyDs7k59+emff939SR6ERpBD2KPpR+0aQiELNDAF+p5lS76tXLAGsJKAQUoDNAGYGhKYDprOCmA2RzzZtasFGGRRTIsouqG7jAuIssI13WA3wGvAyIskkjSrkQfTctKP+6WNXjz0fb+c+nlXNoAS5JOcd9mD/kAAAAASUVORK5CYII=");
}

.currency-flag.currency-flag-cad {
  background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAgCAMAAABjCgsuAAAAVFBMVEX/AAD/AwP/BQX/CQn/R0f/Skr/S0v/Tk7/cHD/cXH/hYX/lJT/q6v/r6//srL/t7f/xMT/zc3/zs7/z8//0ND/1dX/1tb/2Nj/29v/5ub/9/f////rrEQTAAAAbklEQVQ4y+3Ttw6AMAxF0QsJvRMI7f//kwExICFkjwje5uEMbnDJdhOe8oO3g7bVgSGKBxWwYOVgWasUsmpdhKAITAKJCQohqM+qFoIyPIqwlDbdGADTyKeUA+SKsXrXd86rNj3NytNw4/9AHwc7pig+ByeVQt0AAAAASUVORK5CYII=");
}
</style>
